import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const VINYL = {
  title: "Lover Boy 2",
  format: '12" Vinyl + Booklet',
  edition: "Limited Edition",
  price: 37.9,
  currencySymbol: "$",
  dispatch: "7 October",
  image:
    "https://es-ew1-prod-assets.s3.eu-west-1.amazonaws.com/pages/9da6aa0e-b5e4-4738-8a31-ae5c4cd3cc7c/page.jj1SIOT9_1789084037.jpg",
  storeUrl: "https://elasticstage.com/b9ine/releases/lover-boy-2-album",
  tracks: [
    { side: "A", index: 1, title: "Chose You (Die For You)", duration: "2:14", exclusive: true },
    { side: "A", index: 2, title: "Toxic Tether", duration: "2:08" },
    { side: "A", index: 3, title: "Chosen One", duration: "1:56" },
    { side: "A", index: 4, title: "U N ME", duration: "2:10" },
    { side: "A", index: 5, title: "Cope", duration: "2:13" },
    { side: "B", index: 1, title: "Therapy", duration: "3:47" },
    { side: "B", index: 2, title: "Copy", duration: "1:20" },
    { side: "B", index: 3, title: "Can We Go Back?", duration: "2:16" },
    { side: "B", index: 4, title: "My Space", duration: "1:41" },
    { side: "B", index: 5, title: "Slow Down", duration: "2:07" },
  ],
};

const SECRET_VIDEO_ID = "nEmYlDvxYQw";
const SECRET_UNLOCK_SECONDS = 15;

function SecretVideoCard() {
  const cardRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [remaining, setRemaining] = useState(SECRET_UNLOCK_SECONDS);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (!cardRef.current) return;
    const io = new IntersectionObserver(
      (entries) => setInView(entries[0]?.isIntersecting || false),
      { threshold: 0.35 }
    );
    io.observe(cardRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (unlocked || !inView) return;
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          setUnlocked(true);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [inView, unlocked]);

  const progressPct = ((SECRET_UNLOCK_SECONDS - remaining) / SECRET_UNLOCK_SECONDS) * 100;

  return (
    <div
      ref={cardRef}
      data-testid="collector-secret-card"
      className="relative border border-white/10 bg-[#0a0a0a] overflow-hidden aspect-video"
    >
      {unlocked && (
        <motion.iframe
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9 }}
          data-testid="collector-secret-video"
          src={`https://www.youtube.com/embed/${SECRET_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
          title="Bob9ine — Toxic Tether"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full z-10"
        />
      )}
      <motion.div
        initial={false}
        animate={{ opacity: unlocked ? 0 : 1, pointerEvents: unlocked ? "none" : "auto" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6"
      >
        <span className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059]">
          Easter Egg · For Patient Fans
        </span>
        <h3 className="mt-3 font-display text-xl md:text-2xl text-[#F5F5F0]">
          Something unlocks in a moment.
        </h3>
        <div className="mt-5 relative w-16 h-16 grid place-items-center">
          <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
            <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(197,160,89,0.2)" strokeWidth="2" />
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="#C5A059"
              strokeWidth="2"
              strokeDasharray={2 * Math.PI * 46}
              strokeDashoffset={2 * Math.PI * 46 * (1 - progressPct / 100)}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <div className="relative font-display text-xl text-[#F5F5F0] tabular-nums">
            {String(remaining).padStart(2, "0")}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ————————————————————————————————————————————————————————————
// 3D tilt vinyl spotlight
// ————————————————————————————————————————————————————————————
function VinylTilt() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smX = useSpring(mx, { stiffness: 140, damping: 18 });
  const smY = useSpring(my, { stiffness: 140, damping: 18 });
  const rotateY = useTransform(smX, [-0.5, 0.5], [10, -10]);
  const rotateX = useTransform(smY, [-0.5, 0.5], [-8, 8]);

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mx.set(x);
    my.set(y);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateY, rotateX, transformPerspective: 1200 }}
      className="relative aspect-square group"
      data-testid="collector-vinyl-tilt"
    >
      {/* Spinning disc behind */}
      <div className="absolute inset-y-4 -right-6 w-[92%]">
        <div
          className="relative w-full aspect-square rounded-full animate-[spin_18s_linear_infinite]"
          style={{
            background:
              "radial-gradient(circle at center, #1a1a1a 0%, #0a0a0a 30%, #050505 60%, #0a0a0a 100%)",
            boxShadow:
              "0 20px 60px rgba(0,0,0,0.6), inset 0 0 40px rgba(197,160,89,0.08)",
          }}
        >
          <div
            className="absolute inset-0 rounded-full opacity-50"
            style={{
              background:
                "repeating-radial-gradient(circle at center, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 5px)",
            }}
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[26%] aspect-square rounded-full grid place-items-center"
            style={{
              background:
                "radial-gradient(circle at center, #E9CE86 0%, #C5A059 55%, #8a6f33 100%)",
            }}
          >
            <span className="font-display text-[#050505] text-xs md:text-sm">
              LB II
            </span>
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#050505]" />
        </div>
      </div>

      {/* Sleeve */}
      <div className="relative w-[92%] aspect-square overflow-hidden border border-[#C5A059]/50 bg-black shadow-[0_40px_80px_rgba(0,0,0,0.6)]">
        <img
          src={VINYL.image}
          alt={VINYL.title}
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/40 via-transparent to-transparent" />
        {/* Shine sweep */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{
            background:
              "linear-gradient(115deg, transparent 40%, rgba(245,230,168,0.14) 50%, transparent 60%)",
          }}
        />
        <span className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.35em] text-[#F5F5F0] bg-[#050505]/70 border border-[#C5A059]/50 px-2.5 py-1 backdrop-blur-sm">
          {VINYL.edition}
        </span>
        {/* Gold corner marks */}
        <span className="absolute top-0 left-0 w-8 h-px bg-[#C5A059]" />
        <span className="absolute top-0 left-0 w-px h-8 bg-[#C5A059]" />
        <span className="absolute top-0 right-0 w-8 h-px bg-[#C5A059]" />
        <span className="absolute top-0 right-0 w-px h-8 bg-[#C5A059]" />
        <span className="absolute bottom-0 left-0 w-8 h-px bg-[#C5A059]" />
        <span className="absolute bottom-0 left-0 w-px h-8 bg-[#C5A059]" />
        <span className="absolute bottom-0 right-0 w-8 h-px bg-[#C5A059]" />
        <span className="absolute bottom-0 right-0 w-px h-8 bg-[#C5A059]" />
      </div>
    </motion.div>
  );
}

export default function CollectorVinyl() {

  // Just for inside this component scope:
  // (price computed above)
  const price = VINYL.price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <section
      id="collector"
      data-testid="collector-section"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="flex items-end justify-between mb-14 md:mb-20 gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="font-display text-[#C5A059] text-lg md:text-xl tracking-tight">
                Ch. 02
              </span>
              <span className="h-px w-16 bg-[#C5A059]/40" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#C5A059]">
                Collector
              </span>
            </div>
            <h2 className="font-display uppercase font-semibold tracking-tighter text-[#F5F5F0] text-4xl md:text-6xl">
              Vinyl <span className="text-[#C5A059]">Edition</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm text-[#F5F5F0]/60 leading-relaxed">
            A limited pressing of <span className="text-[#F5F5F0]">Lover Boy 2</span>{" "}
            — with one exclusive cut and a sleeve worth hanging on the wall.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Vinyl visual */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <VinylTilt />
          </motion.div>

          {/* Product details */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 flex flex-col"
          >
            {/* Stock urgency */}
            <div
              data-testid="collector-stock"
              className="inline-flex items-center gap-2.5 self-start border border-[#B31B1B] bg-[#B31B1B]/10 px-3 py-1.5 mb-5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#B31B1B] live-dot" />
              <span className="text-[10px] uppercase tracking-[0.35em] text-[#F5F5F0]">
                Almost Out of Stock
              </span>
            </div>

            <div className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
              Physical · {VINYL.format}
            </div>
            <h3 className="mt-2 font-display text-3xl md:text-4xl text-[#F5F5F0] leading-tight">
              {VINYL.title}
            </h3>

            <div className="mt-6 flex items-end justify-between gap-6 border-t border-white/10 pt-5">
              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#F5F5F0]/40">
                  Price
                </div>
                <div
                  data-testid="collector-price"
                  className="font-display text-4xl text-[#F5F5F0] tabular-nums"
                >
                  <span className="text-[#C5A059] text-xl align-top">
                    {VINYL.currencySymbol}
                  </span>
                  {price}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#F5F5F0]/40">
                  Ships
                </div>
                <div className="mt-1 font-display text-xl text-[#F5F5F0]">
                  {VINYL.dispatch}
                </div>
              </div>
            </div>

            {/* Tracklist (compact, 2-column) */}
            <div className="mt-6">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#F5F5F0]/40 mb-3">
                10 Tracks · 21:52
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {VINYL.tracks.map((t) => (
                  <li
                    key={`${t.side}-${t.index}`}
                    className="flex items-center gap-2 text-xs text-[#F5F5F0]/80"
                  >
                    <span className="w-6 text-[9px] uppercase tracking-[0.2em] text-[#C5A059]/70">
                      {t.side}
                      {t.index}
                    </span>
                    <span className="flex-1 truncate flex items-center gap-2">
                      {t.title}
                      {t.exclusive && (
                        <span className="text-[8px] uppercase tracking-[0.3em] text-[#B31B1B] border border-[#B31B1B]/50 px-1 py-0.5">
                          Excl.
                        </span>
                      )}
                    </span>
                    <span className="text-[#F5F5F0]/35 tabular-nums text-[10px]">
                      {t.duration}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Perks */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="border border-white/10 bg-[#0a0a0a] p-3">
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#C5A059]">Hang It</div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-[#F5F5F0]/60">
                  Sleeve doubles as gallery-grade wall art.
                </p>
              </div>
              <div className="border border-white/10 bg-[#0a0a0a] p-3">
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#C5A059]">Spin It</div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-[#F5F5F0]/60">
                  Full 10 tracks + one vinyl / SoundCloud only cut.
                </p>
              </div>
            </div>

            {/* Buy CTA */}
            <a
              href={VINYL.storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="collector-buy"
              className="group/buy mt-7 relative inline-flex items-stretch overflow-hidden border border-[#B31B1B] bg-[#B31B1B] w-full transition-colors duration-500 hover:bg-transparent"
            >
              <span className="flex-1 px-5 py-4 flex items-center justify-between gap-4">
                <span className="font-display text-base md:text-lg text-[#F5F5F0] group-hover/buy:text-[#B31B1B] transition-colors duration-500">
                  Buy Now
                </span>
                <span className="text-[10px] uppercase tracking-[0.35em] text-[#F5F5F0]/70 group-hover/buy:text-[#B31B1B]/70 transition-colors duration-500 tabular-nums">
                  {VINYL.currencySymbol}
                  {price}
                </span>
              </span>
              <span className="w-14 shrink-0 bg-[#050505] group-hover/buy:bg-[#B31B1B] grid place-items-center text-[#B31B1B] group-hover/buy:text-[#F5F5F0] transition-colors duration-500 text-xl">
                <span className="inline-block transition-transform duration-500 group-hover/buy:translate-x-1">
                  →
                </span>
              </span>
            </a>
            <p className="mt-2 text-[9px] uppercase tracking-[0.3em] text-[#F5F5F0]/35">
              Secure checkout via ElasticStage
            </p>
          </motion.div>
        </div>

        {/* Easter egg video — tucked in subtly at bottom of section */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 md:mt-24 max-w-3xl mx-auto"
        >
          <SecretVideoCard />
        </motion.div>
      </div>
    </section>
  );
}
